/**
 * 관리자 화면 기능 권한 카탈로그.
 *
 * 서버 `src/user/admin-permission.ts` 와 키가 1:1 로 맞아야 한다. 서버 가드(AdminAccessGuard)가
 * 이 표로 API 를 막고, 화면은 같은 표로 메뉴·라우트·버튼을 가린다 — 화면이 가리는 것은 편의이고
 * 막는 것은 서버다.
 *
 * 역할
 *  - admin (최고 관리자): 전부 + 사용자 관리·로그인 세션. 권한표를 보지 않는다
 *  - operator (운영자): 영역마다 없음 / 조회 / 관리
 *  - viewer (조회 전용): 영역마다 없음 / 조회 (관리는 서버가 조회로 깎는다)
 */
import type { UserRole } from '@/types/auth';

export const ADMIN_AREA_KEYS = [
  'kiosks',
  'kiosk_control',
  'device_gate',
  'institutions',
  'institution_api',
  'contents',
  'complaints',
  'analytics',
  'consent_documents',
] as const;

export type AdminAreaKey = (typeof ADMIN_AREA_KEYS)[number];
export type AdminAccessLevel = 'none' | 'read' | 'manage';
export type AdminPermissions = Partial<Record<AdminAreaKey, Exclude<AdminAccessLevel, 'none'>>>;

export interface AdminAreaInfo {
  key: AdminAreaKey;
  label: string;
  /** 이 영역에 드는 화면 (권한표 설명) */
  screens: string;
  /** 관리 권한이 여는 일 */
  manageHint: string;
}

export const ADMIN_AREAS: AdminAreaInfo[] = [
  {
    key: 'kiosks',
    label: '키오스크',
    screens: '키오스크 관리·상세, 키오스크 설정, 화면 확인',
    manageHint: '정보 수정·기관 배정·삭제, 미디어 권한, 설정 변경',
  },
  {
    key: 'kiosk_control',
    label: '원격 제어',
    screens: '원격 세션, 일괄 명령 기록, 패치 버전',
    manageHint: '원격 셸 접속, 명령 실행, 패치 배포, SSH 키 교체',
  },
  {
    key: 'device_gate',
    label: '출입 관리',
    screens: '키오스크 설정 › 출입 관리 (판정 현황·무단 접속·출입증)',
    manageHint: '출입증·데모 키 발급과 폐기',
  },
  {
    key: 'institutions',
    label: '기관',
    screens: '기관 관리, 기관 제공 동의, 회원 정보',
    manageHint: '기관 만들기·수정·삭제, 동의 변경, 데이터 이관',
  },
  {
    key: 'institution_api',
    label: '기관 API',
    screens: '기관 API 키, API 접근 기록',
    manageHint: 'API 키 발급·재발급·폐기',
  },
  {
    key: 'contents',
    label: '콘텐츠',
    screens: '콘텐츠 카탈로그, 순간기록 관리',
    manageHint: '콘텐츠 등록·수정·배정, 순간기록 삭제',
  },
  {
    key: 'complaints',
    label: '민원·문의',
    screens: '문의 관리, 민원·문의 접수, 민원 통계',
    manageHint: '문의 답변·상태 변경',
  },
  {
    key: 'analytics',
    label: '분석',
    screens: '애널리틱스, 기관 리포트, rPPG 로그, PDF 리포트',
    manageHint: '회원 메모 작성',
  },
  {
    key: 'consent_documents',
    label: '약관·동의서',
    screens: '약관·동의서 관리 (기관별 이용약관·개인정보·기관 데이터 제공 동의와 개정 이력)',
    manageHint: '새 버전 게시, 옛 버전 되돌리기, 기관 개별본 해제',
  },
];

export const ADMIN_AREA_BY_KEY = Object.fromEntries(
  ADMIN_AREAS.map((area) => [area.key, area]),
) as Record<AdminAreaKey, AdminAreaInfo>;

export const ACCESS_LEVEL_LABELS: Record<AdminAccessLevel, string> = {
  none: '없음',
  read: '조회',
  manage: '관리',
};

const RANK: Record<AdminAccessLevel, number> = { none: 0, read: 1, manage: 2 };

export const isAdminPanelRole = (role: UserRole | null | undefined): boolean =>
  role === 'admin' || role === 'operator' || role === 'viewer';

/** 역할이 받을 수 있는 가장 높은 단계 */
export const roleCeiling = (role: UserRole | null | undefined): AdminAccessLevel =>
  role === 'admin' || role === 'operator' ? 'manage' : role === 'viewer' ? 'read' : 'none';

/** 서버 normalizePermissions 와 같은 규칙 — 모르는 키는 버리고 역할 상한으로 깎는다 */
export const normalizePermissions = (
  value: unknown,
  role: UserRole | null | undefined,
): AdminPermissions => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const ceiling = RANK[roleCeiling(role)];
  const result: AdminPermissions = {};
  for (const key of ADMIN_AREA_KEYS) {
    const level = (value as Record<string, unknown>)[key];
    if (level !== 'read' && level !== 'manage') continue;
    const rank = Math.min(RANK[level], ceiling);
    if (rank > 0) result[key] = rank === 2 ? 'manage' : 'read';
  }
  return result;
};

export const levelOf = (
  role: UserRole | null | undefined,
  permissions: AdminPermissions | null | undefined,
  area: AdminAreaKey,
): AdminAccessLevel => {
  if (role === 'admin') return 'manage';
  return normalizePermissions(permissions, role)[area] ?? 'none';
};

export const canAccess = (
  role: UserRole | null | undefined,
  permissions: AdminPermissions | null | undefined,
  area: AdminAreaKey,
  level: Exclude<AdminAccessLevel, 'none'> = 'read',
): boolean => RANK[levelOf(role, permissions, area)] >= RANK[level];

/** 권한표를 비워 계정을 만들 때 서버가 넣는 기본값과 같다 */
export const DEFAULT_PANEL_PERMISSIONS: AdminPermissions = {
  kiosks: 'read',
  analytics: 'read',
};

/**
 * 관리자 화면 경로 → 그 화면을 여는 데 필요한 영역.
 * 긴 접두사가 먼저 맞아야 한다 (`/kiosks/commands` 가 `/kiosks` 보다 앞).
 * null 은 최고 관리자 전용(사용자 관리), 목록에 없는 경로(설정 등)는 누구나 연다.
 */
const ROUTE_AREAS: [prefix: string, area: AdminAreaKey | null][] = [
  ['/kiosks/commands', 'kiosk_control'],
  ['/kiosks/patches', 'kiosk_control'],
  ['/kiosks/settings', 'kiosks'],
  ['/kiosks', 'kiosks'],
  ['/sessions', 'kiosk_control'],
  ['/remote', 'kiosk_control'],
  ['/institutions/api-keys', 'institution_api'],
  ['/institutions/access-logs', 'institution_api'],
  ['/institutions/moments', 'contents'],
  ['/institutions/member-consent', 'institutions'],
  ['/institutions/consent-documents', 'consent_documents'],
  ['/institutions', 'institutions'],
  ['/contents', 'contents'],
  ['/inquiries', 'complaints'],
  ['/complaints', 'complaints'],
  ['/analytics', 'analytics'],
  ['/users', null],
];

export type RouteRequirement = { kind: 'open' } | { kind: 'admin' } | { kind: 'area'; area: AdminAreaKey };

export const routeRequirement = (pathname: string): RouteRequirement => {
  for (const [prefix, area] of ROUTE_AREAS) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) {
      return area ? { kind: 'area', area } : { kind: 'admin' };
    }
  }
  return { kind: 'open' };
};

export const canOpenRoute = (
  role: UserRole | null | undefined,
  permissions: AdminPermissions | null | undefined,
  pathname: string,
): boolean => {
  const req = routeRequirement(pathname);
  if (req.kind === 'open') return true;
  if (req.kind === 'admin') return role === 'admin';
  return canAccess(role, permissions, req.area);
};

/** 첫 화면 후보. 열 수 있는 첫 경로로 보낸다 — 아무것도 없으면 설정 */
const LANDING_PATHS = ['/kiosks', '/analytics', '/complaints', '/inquiries', '/institutions', '/contents', '/sessions'];

export const landingPath = (
  role: UserRole | null | undefined,
  permissions: AdminPermissions | null | undefined,
): string => LANDING_PATHS.find((path) => canOpenRoute(role, permissions, path)) ?? '/settings';
